import api from '../api'
import {
  IMAGE_ONLY_MODERATION_TEXT,
  PROMPT_SAFETY_PROMPT_ID,
  PROMPT_SAFETY_PROMPT_VERSION,
} from '@/scenes/engine/common/promptConstants'

export type AiModerationCategory =
  | 'harassment'
  | 'harassment/threatening'
  | 'hate'
  | 'hate/threatening'
  | 'illicit'
  | 'illicit/violent'
  | 'self-harm'
  | 'self-harm/intent'
  | 'self-harm/instructions'
  | 'sexual'
  | 'sexual/minors'
  | 'violence'
  | 'violence/graphic'

export type AiModerationReason =
  | 'sexual'
  | 'sexual_minors'
  | 'graphic_violence'
  | 'violence'
  | 'self_harm'
  | 'hate'
  | 'illicit'
  | 'harassment'
  | 'other'

export interface AiModerationResultItem {
  flagged: boolean
  categories: Partial<Record<AiModerationCategory, boolean>>
  category_scores?: Partial<Record<AiModerationCategory, number>>
  category_applied_input_types?: Partial<Record<AiModerationCategory, string[]>>
}

export interface AiModerationResponse {
  id?: string
  model?: string
  results: AiModerationResultItem[]
}

export interface AiCombinedModerationResult {
  isFlagged: boolean
  isSafeForWork: boolean
  reason: AiModerationReason | null
  response: AiModerationResponse | null
}

function parsePromptSafetyResponse(response: any): boolean {
  const sanitizedResponse =
    typeof response === 'string'
      ? response.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '')
      : response

  const parsed = typeof sanitizedResponse === 'string' ? JSON.parse(sanitizedResponse) : sanitizedResponse
  if (typeof parsed?.is_safe_for_work !== 'boolean') {
    throw new Error('Invalid prompt safety response')
  }

  return parsed.is_safe_for_work
}

function getModerationReason(
  moderationResponse: AiModerationResponse | null,
  isSafeForWork: boolean
): AiModerationReason | null {
  const hasCategory = (category: AiModerationCategory): boolean =>
    Boolean(moderationResponse?.results?.some(result => result.categories?.[category] === true))

  if (hasCategory('sexual/minors')) return 'sexual_minors'
  if (hasCategory('sexual')) return 'sexual'
  if (hasCategory('violence/graphic')) return 'graphic_violence'
  if (hasCategory('violence')) return 'violence'
  if (hasCategory('self-harm') || hasCategory('self-harm/intent') || hasCategory('self-harm/instructions')) {
    return 'self_harm'
  }
  if (hasCategory('hate') || hasCategory('hate/threatening')) return 'hate'
  if (hasCategory('illicit') || hasCategory('illicit/violent')) return 'illicit'
  if (hasCategory('harassment') || hasCategory('harassment/threatening')) return 'harassment'

  if (!isSafeForWork) return 'other'

  const hasAnyFlagged = Boolean(moderationResponse?.results?.some(result => result.flagged))
  return hasAnyFlagged ? 'other' : null
}

export async function classifyPromptSafety(text: string, abortSignal?: AbortSignal): Promise<boolean> {
  const response = await api.openAiResponses({
    promptId: PROMPT_SAFETY_PROMPT_ID,
    version: PROMPT_SAFETY_PROMPT_VERSION,
    input: [text],
    abortSignal,
  })

  return parsePromptSafetyResponse(response)
}

async function runModerateContent(text: string, base64image: string, abortSignal?: AbortSignal): Promise<AiModerationResponse> {
  const data = await api.moderateContent(text, base64image, abortSignal)
  if (!Array.isArray(data?.results)) {
    throw new Error('Invalid moderation response')
  }
  return data as AiModerationResponse
}

export async function moderateAi(
  text: string,
  base64image: string | null,
  options?: { skipPromptSafety?: boolean; abortSignal?: AbortSignal }
): Promise<AiCombinedModerationResult> {
  const { skipPromptSafety = false, abortSignal } = options ?? {}
  const shouldRunContentModeration = Boolean(base64image?.trim())
  const shouldRunPromptSafety = !skipPromptSafety

  const moderationPromise = shouldRunContentModeration
    ? runModerateContent(text, base64image as string, abortSignal)
    : Promise.resolve<AiModerationResponse | null>(null)
  const promptSafetyPromise = shouldRunPromptSafety
    ? classifyPromptSafety(text, abortSignal)
    : Promise.resolve(true)

  const [moderationResponse, isSafeForWork] = await Promise.all([moderationPromise, promptSafetyPromise])
  const moderationFlagged = Boolean(moderationResponse?.results?.some(result => result.flagged))
  const isFlagged = moderationFlagged || !isSafeForWork
  const reason = getModerationReason(moderationResponse, isSafeForWork)

  return {
    isFlagged,
    isSafeForWork,
    reason,
    response: moderationResponse,
  }
}

/** Image-only NSFW gate. The moderation API still requires a non-empty `text` field. */
export async function moderateImageOnly(
  base64image: string,
  abortSignal?: AbortSignal,
): Promise<AiCombinedModerationResult> {
  return moderateAi(IMAGE_ONLY_MODERATION_TEXT, base64image, {
    skipPromptSafety: true,
    abortSignal,
  })
}
